import { type ReactElement, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useAuthStore } from '@/entities/user';
import { routeForStatus, SignInForm, SocialSignInButtons } from '@/features/auth';
import { GuestQuoteOrderHint } from '@/features/guest-quote-claim';
import { AuthPageShell } from '@/widgets/auth-page-shell';

/** 로그인(/sign-in) — 전체 화면 인증 틀 위에 폼, 비밀번호 찾기·회원가입 한 줄, 간편 로그인. */
export const SignInPage = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  // 이미 로그인한 채로 들어오면 폼을 보이지 않고 상태에 맞는 곳으로 보낸다. 마운트 때만 본다 —
  // 로그인 성공으로 세션이 생기는 순간의 이동은 handleSuccess 가 원래 목적지까지 책임진다.
  const [redirectTo] = useState<string | null>(() => {
    const { isAuthenticated, user } = useAuthStore.getState();
    if (!isAuthenticated) return null;
    return user ? routeForStatus(user.status) : '/';
  });

  const handleSuccess = (): void => {
    const status = useAuthStore.getState().user?.status;

    // 온보딩이 남았으면 그쪽이 우선이고, 아니면 가드가 넘겨준 원래 목적지로 돌아간다.
    const statusRoute = status ? routeForStatus(status) : '/';
    const from = (location.state as { from?: string } | null)?.from;

    void navigate(statusRoute !== '/' ? statusRoute : (from ?? '/'), { replace: true });
  };

  if (redirectTo !== null) return <Navigate to={redirectTo} replace />;

  return (
    <AuthPageShell
      title="Sign in"
      screenTitle="로그인"
      description="계정에 로그인하여 계속하세요."
      brandHeadline={
        <>
          디자인부터 확정견적까지,
          <br />
          굿즈 제작을 한 곳에서.
        </>
      }
      brandDescription="로그인하고 디자인을 저장하거나 견적을 확인하세요."
    >
      {/* 링크 화면 [주문하기]로 왔으면 가입·인증 뒤 견적이 연결된다고 알린다(결정 G11). 돌아갈 곳은 state.from 의 링크 경로다. */}
      <GuestQuoteOrderHint />
      <SignInForm onSuccess={handleSuccess} />

      <div className="mt-5 flex items-center justify-between text-[13px]">
        <Link
          to="/reset-password"
          className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          비밀번호 찾기
        </Link>
        <Link to="/sign-up" className="font-semibold underline-offset-4 hover:underline">
          회원가입
        </Link>
      </div>

      <div className="mt-10 border-t border-foreground/10 pt-8">
        <SocialSignInButtons />
      </div>
    </AuthPageShell>
  );
};
